import { type Accessor, createMemo } from "solid-js";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useListTodoCountsQuery } from "@listto/shared/solid";
import { mapListTodoCounts } from "@listto/shared";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

interface UseListTodoCountsParams {
  userId: Accessor<string | undefined>;
  enabled?: Accessor<boolean>;
}

/**
 * Lightweight hook that returns todo counts per list using a single GROUP BY.
 * Delegates to the shared `useListTodoCountsQuery` domain hook so the queryKey
 * is always `queryKeys.lists.todoCounts(userId)` — never an inline string.
 */
export function useListTodoCounts(params: UseListTodoCountsParams) {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();

  const query = useListTodoCountsQuery({
    db: () => db(),
    drizzle,
    userId: params.userId,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    throttleTime: 300,
  });

  // Re-apply the enabled guard: the domain hook enables by default when userId
  // and drizzle are truthy. If the caller passes an extra `enabled` signal we
  // honour it by returning an empty map when disabled.
  const isEnabled = params.enabled ?? (() => true);

  const counts = createMemo(() => {
    if (!isEnabled()) {
      return new Map<string, number>();
    }
    return mapListTodoCounts(query.data ?? []);
  });

  return {
    data: counts,
    isLoading: () => query.isPending,
    isError: () => query.isError,
  };
}
